import Datatable from '@/components/common/Datatable';
import { useGetReportsByReview } from '@/services/reviews';
import { IReport, IReview } from '@/services/reviews/types';
import {
  Box,
  Divider,
  Flex,
  HStack,
  // Icon,
  Image,
  // Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { Column } from 'react-table';
// import deleteIcon from "../../../../assets/svg/delete.svg?react";
// import useCustomToast from "@/components/common/Toast";
// import useErrorHandler from "@/utils/errorHandler";
// import { AxiosError } from "axios";

interface IReportsByReviewProps {
  review: IReview | null;
}

const ReportsByReview: React.FC<IReportsByReviewProps> = ({ review }) => {
  const { user_first_name, user_last_name, id, product_images, product_description, product_name } =
    review!;

  const firstName = user_first_name?.trim() || '';
  const lastName = user_last_name?.trim() || '';
  const customerName = [firstName, lastName].filter(Boolean).join(' ');

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  // const { showToast } = useCustomToast();
  // const { handleError } = useErrorHandler();
  // const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data: reports,
    isLoading,
    isError,
    refetch,
  } = useGetReportsByReview({
    id: id?.toString() ?? '',
    page: pagination.page,
  });

  // const { mutateAsync: deleteReport } = useDeleteReport();

  // const handleDelete = async (reportId: string) => {
  //   if (!reportId) return;
  //   setDeletingId(reportId);

  //   try {
  //     await deleteReport(reportId);
  //     showToast("success", "Report deleted successfully");
  //   } catch (error: unknown) {
  //     if (error instanceof AxiosError) {
  //       handleError(error);
  //     }
  //   } finally {
  //     setTimeout(() => {
  //       setDeletingId(null);
  //     }, 500);
  //   }
  // };

  useEffect(() => {
    if (reports) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 5,
        totalCount: reports.count,
        totalPages: reports.page_count,
      }));
    }
  }, [reports, refetch]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const columns: Column<IReport>[] = useMemo(
    () => [
      {
        Header: 'Customer Name',
        Cell: ({ row }: { row: { original: IReport } }) => {
          const { user_first_name, user_last_name } = row.original;
          const firstName = user_first_name?.trim() || '';
          const lastName = user_last_name?.trim() || '';
          const customerName = [firstName, lastName].filter(Boolean).join(' ');
          return customerName !== '' ? (
            <Text minWidth={'200px'}>{customerName}</Text>
          ) : (
            <HStack minWidth={'200px'}>
              <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
            </HStack>
          );
        },
      },
      {
        Header: 'Reason',
        Cell: ({ row }: { row: { original: IReport } }) => {
          const reason = row.original.reason
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          return reason ? (
            <Text minWidth={'200px'}>
              {reason}
              <Text as={'span'} color={'text.secondary'}>
                : {row.original.description}
              </Text>
            </Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Created at',
        Cell: ({ row }: { row: { original: IReport } }) => {
          const createdAt = row.original.created_at;
          return createdAt ? (
            <Text minWidth={'200px'}>{createdAt}</Text>
          ) : (
            <HStack minWidth={'200px'}>
              <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
            </HStack>
          );
        },
      },
      // {
      //   Header: "Actions",
      //   Cell: ({ row }: { row: { original: IReport } }) => {
      //     const isDeleting = deletingId === row.original.id?.toString();
      //     return (
      //       <Box minWidth="20px" minHeight="20px">
      //         {isDeleting ? (
      //           <Spinner size="sm" color="status.error" />
      //         ) : (
      //           <Icon
      //             as={deleteIcon}
      //             boxSize={5}
      //             color={"status.error"}
      //             cursor={"pointer"}
      //             onClick={() =>
      //               handleDelete(row.original.id?.toString() ?? "")
      //             }
      //           />
      //         )}
      //       </Box>
      //     );
      //   },
      // },
    ],
    [reports],
  );

  return (
    <Flex direction={'column'} width={'100%'} gap={6} fontFamily={'primary'}>
      <Text color={'text.primary'}>
        Customer Name:{' '}
        <Text as={'span'} color={'text.secondary'}>
          {customerName ?? ' -'}
        </Text>
      </Text>

      <Flex justifyContent={'space-between'} alignItems={'top'}>
        <HStack gap={4} alignItems={'top'}>
          <Image
            boxSize="60px"
            objectFit="cover"
            src={product_images?.[0] ?? ''}
            alt={review?.headline}
            objectPosition={'top'}
            borderRadius={'lg'}
          />
          <VStack align="start" maxWidth="100%" spacing={1}>
            <Text isTruncated maxWidth="100%" fontSize={'md'} color={'text.primary'}>
              {product_name ?? ''}
            </Text>
            <Text
              maxWidth="100%"
              wordBreak="break-word"
              overflowWrap="break-word"
              whiteSpace="normal"
              color={'text.secondary'}
              fontSize={'xs'}
              noOfLines={2}
            >
              {product_description ?? ''}
            </Text>
          </VStack>
        </HStack>
        <HStack gap={4}>4/5</HStack>
      </Flex>

      <Divider borderColor={'border.default'} />

      <Text color={'text.primary'}>
        Review:{' '}
        {review?.review_text ? (
          <Text as={'span'} color={'text.secondary'}>
            {review?.review_text}
          </Text>
        ) : (
          <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
        )}
      </Text>

      <Box borderRadius={'xl'}>
        <Datatable
          data={reports?.results ?? []}
          isLoading={isLoading}
          isError={isError}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
            totalPages: pagination.totalPages,
          }}
          onPageChange={handlePageChange}
          // sticky={true}
          hideFilters={true}
          headerText="Reports"
        />
      </Box>
    </Flex>
  );
};

export default ReportsByReview;
